import { useState, useEffect } from 'react';
import { useAuth } from '../../../AuthContext';
import { fetchData } from '../../../apiService';
import { useSessionValidation } from '../../../utils/sessionValidation';
import {
    Refresh, Print, GetApp, Search as SearchIcon, ArrowBack
} from '@mui/icons-material';
import {
    Button, Tooltip, Alert, Snackbar, Box, Typography, CircularProgress,
    Fade, TextField, FormControl, Select, MenuItem,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    Dialog, DialogTitle, DialogContent, DialogActions, Grid, Chip, IconButton, Divider,
    Autocomplete
} from '@mui/material';
import { useHistory } from 'react-router-dom';
import { Close as CloseIcon } from '@mui/icons-material';
import './LedgerReport.css';
import SaleModal from '../Sale/SaleModal';
import PurchaseModal from '../Purchase/PurchaseModal';
import PaymentReceiptsModal from '../PaymentReceipts/PaymentReceiptsModal';
import JournalEntryModal from '../JournalEntry/JournalEntryModal';

const LedgerReport = () => {
    // Session validation
    useSessionValidation();

    const { companyId, branchId, token, username, companyName } = useAuth();
    const history = useHistory();

    // Date range states
    const formatDateToYYYYMMDD = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const getDefaultFromDate = () => {
        const today = new Date();
        const currentYear = today.getFullYear();
        const currentMonth = today.getMonth(); // 0-indexed (0 = January, 3 = April)

        // Financial year starts on April 1st
        // If current month is January-March (0-2), use previous year's April 1st
        // If current month is April-December (3-11), use current year's April 1st
        const financialYearStart = currentMonth < 3
            ? new Date(currentYear - 1, 3, 1) // April 1st of previous year
            : new Date(currentYear, 3, 1);     // April 1st of current year

        return formatDateToYYYYMMDD(financialYearStart);
    };

    const getCurrentDate = () => formatDateToYYYYMMDD(new Date());

    const [fromDate, setFromDate] = useState(getDefaultFromDate());
    const [toDate, setToDate] = useState(getCurrentDate());

    // Data states
    const [ledgerData, setLedgerData] = useState([]);
    const [masterTypes, setMasterTypes] = useState([]);
    const [masters, setMasters] = useState([]);
    const [filteredMasters, setFilteredMasters] = useState([]);
    const [selectedMasterType, setSelectedMasterType] = useState('');
    const [selectedMaster, setSelectedMaster] = useState('');

    // Modal states
    const [openSaleModal, setOpenSaleModal] = useState(false);
    const [openRentInvoiceModal, setOpenRentInvoiceModal] = useState(false);
    const [openPurchaseModal, setOpenPurchaseModal] = useState(false);
    const [openPaymentModal, setOpenPaymentModal] = useState(false);
    const [selectedRecordId, setSelectedRecordId] = useState(null);
    const [rentInvoiceData, setRentInvoiceData] = useState(null);

    // Modal data states (required for SaleModal)
    const [customers, setCustomers] = useState([]);
    const [items, setItems] = useState([]);
    const [banks, setBanks] = useState([]);
    const [saleFormData, setSaleFormData] = useState({});
    const [saleDetails, setSaleDetails] = useState([]);

    // Purchase modal states
    const [purchaseFormData, setPurchaseFormData] = useState({});
    const [purchaseDetails, setPurchaseDetails] = useState([]);
    const [suppliers, setSuppliers] = useState([]);

    // Payment/Receipt modal states
    const [paymentFormData, setPaymentFormData] = useState({});
    const [paymentReceiptDetails, setPaymentReceiptDetails] = useState([]);
    const [paymentMasters, setPaymentMasters] = useState([]);
    const [paymentMethods, setPaymentMethods] = useState([]);

    // Journal Entry modal states
    const [openJournalModal, setOpenJournalModal] = useState(false);
    const [journalFormData, setJournalFormData] = useState({});
    const [journalEntryDetails, setJournalEntryDetails] = useState([]);
    const [journalMasters, setJournalMasters] = useState([]);

    // Purchase Return view modal states
    const [openPurchaseReturnModal, setOpenPurchaseReturnModal] = useState(false);
    const [purchaseReturnData, setPurchaseReturnData] = useState(null);
    const [purchaseReturnDetails, setPurchaseReturnDetails] = useState([]);

    // Loading and error states
    const [loading, setLoading] = useState(false);
    const [loadingMasters, setLoadingMasters] = useState(false); // Separate loading state for masters dropdown
    const [loadingLedger, setLoadingLedger] = useState(false); // Separate loading state for ledger data
    const [modalLoading, setModalLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('Loading...');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        setMounted(true);
    }, []);

    useEffect(() => {
        if (companyId && token) {
            fetchMasterTypes();
        }
    }, [companyId, token]);

    useEffect(() => {
        if (selectedMasterType) {
            // Fetch masters based on selected master type
            fetchMastersByType(selectedMasterType);
            setSelectedMaster(''); // Reset selected master when master type changes
        } else {
            setFilteredMasters([]);
            setSelectedMaster('');
        }
    }, [selectedMasterType]);

    // Removed auto-fetch on dropdown change to prevent page blink
    // User must click "Get Ledger" button to fetch data

    // Fetch functions
    const fetchMasterTypes = async () => {
        try {
            setLoading(true);
            setLoadingMessage('Loading master types...');
            const response = await fetchData('MasterTypeApi', `/${companyId}`, token);
            setMasterTypes(response.response_data || response || []);
        } catch (error) {
            console.error('Error fetching master types:', error);

            // For demo purposes, create sample master types if API is not available
            const sampleMasterTypes = [
                { Id: 1, Name: 'Client And Vendor' },
                { Id: 2, Name: 'AccountHead' },
                { Id: 3, Name: 'Assets' },
                { Id: 4, Name: 'Capital' },
                { Id: 5, Name: 'Bank' },
                { Id: 6, Name: 'Other' },
                { Id: 7, Name: 'Staff' }
            ];

            setMasterTypes(sampleMasterTypes);
        } finally {
            setLoading(false);
        }
    };

    const fetchMasters = async () => {
        try {
            setLoading(true);
            setLoadingMessage('Loading masters...');
            const response = await fetchData('ClientAndVendorApi', `/ClientList/${companyId}`, token);
            setMasters(response.response_data || response || []);
        } catch (error) {
            console.error('Error fetching masters:', error);

            // For demo purposes, create sample masters if API is not available
            const sampleMasters = [
                { MasterId: 1, Name: 'ABC Suppliers Pvt Ltd', MasterTypeId: 2 },
                { MasterId: 2, Name: 'XYZ Trading Company', MasterTypeId: 2 },
                { MasterId: 3, Name: 'Federal Bank', MasterTypeId: 3 },
                { MasterId: 4, Name: 'State Bank Of India', MasterTypeId: 3 },
                { MasterId: 5, Name: 'Cash Account', MasterTypeId: 4 },
                { MasterId: 6, Name: 'Sales Account', MasterTypeId: 4 },
                { MasterId: 7, Name: 'Purchase Account', MasterTypeId: 4 },
                { MasterId: 8, Name: 'John Doe', MasterTypeId: 1 },
                { MasterId: 9, Name: 'Jane Smith', MasterTypeId: 1 }
            ];

            setMasters(sampleMasters);
            setSuccess('Demo masters loaded (API not available)');
        } finally {
            setLoading(false);
        }
    };

    const fetchMastersByType = async (masterTypeId) => {
        try {
            setLoadingMasters(true); // Use separate loading state to avoid page blink
            setLoadingMessage('Loading masters...');

            let apiEndpoint = '';
            let apiController = '';

            // Determine which API to call based on Master Type
            const masterType = masterTypes.find(type => type.Id === parseInt(masterTypeId));
            const masterTypeName = masterType?.Name?.toLowerCase() || '';

            console.log('Selected Master Type:', masterTypeName, 'ID:', masterTypeId);

            if (masterTypeName === 'client and vendor') {
                apiController = 'ClientAndVendorApi';
                apiEndpoint = `/${companyId}`;
            } else if (masterTypeName === 'accounthead') {
                apiController = 'IncomeExpenseApi';
                apiEndpoint = `/${companyId}`;
            } else if (masterTypeName === 'assets') {
                apiController = 'AssetsApi';
                apiEndpoint = `/${companyId}`;
            } else if (masterTypeName === 'capital') {
                apiController = 'CapitalApi';
                apiEndpoint = `/${companyId}`;
            } else if (masterTypeName === 'bank') {
                apiController = 'BankApi';
                apiEndpoint = `/${companyId}`;
            } else if (masterTypeName === 'other') {
                apiController = 'OtherApi';
                apiEndpoint = `/${companyId}`;
            } else if (masterTypeName === 'staff') {
                apiController = 'StaffApi';
                apiEndpoint = `/${companyId}`;
            } else {
                // Default fallback
                apiController = 'ClientAndVendorApi';
                apiEndpoint = `/${companyId}`;
            }

            console.log('Fetching from:', apiController, apiEndpoint);

            const response = await fetchData(apiController, apiEndpoint, token);
            console.log('API Response:', response);

            const mastersList = response.response_data || response || [];
            console.log('Masters List:', mastersList);

            // Map the response to a consistent format
            // Handle different possible field names from API
            const formattedMasters = mastersList.map(master => ({
                MasterId: master.Id || master.MasterId || master.id,
                Name: master.Name || master.name || master.ClientName || master.VendorName || master.BankName || master.AccountHeadName || master.StaffName || master.AssetName || master.CapitalName,
                MasterTypeId: parseInt(masterTypeId)
            }));

            console.log('Formatted Masters:', formattedMasters);

            setFilteredMasters(formattedMasters);

            if (formattedMasters.length === 0) {
                setSuccess('No masters found for selected type');
            }

        } catch (error) {
            console.error('Error fetching masters by type:', error);

            // For demo purposes, create sample data based on master type
            const masterType = masterTypes.find(type => type.Id === parseInt(masterTypeId));
            const masterTypeName = masterType?.Name?.toLowerCase() || '';

            let sampleMasters = [];

            if (masterTypeName === 'client and vendor') {
                sampleMasters = [
                    { MasterId: 1, Name: 'ABC Suppliers Pvt Ltd', MasterTypeId: parseInt(masterTypeId) },
                    { MasterId: 2, Name: 'XYZ Trading Company', MasterTypeId: parseInt(masterTypeId) },
                    { MasterId: 3, Name: 'John Doe', MasterTypeId: parseInt(masterTypeId) },
                    { MasterId: 4, Name: 'Jane Smith', MasterTypeId: parseInt(masterTypeId) }
                ];
            } else if (masterTypeName === 'accounthead') {
                sampleMasters = [
                    { MasterId: 5, Name: 'Cash Account', MasterTypeId: parseInt(masterTypeId) },
                    { MasterId: 6, Name: 'Sales Account', MasterTypeId: parseInt(masterTypeId) },
                    { MasterId: 7, Name: 'Purchase Account', MasterTypeId: parseInt(masterTypeId) },
                    { MasterId: 8, Name: 'Expense Account', MasterTypeId: parseInt(masterTypeId) }
                ];
            } else if (masterTypeName === 'assets') {
                sampleMasters = [
                    { MasterId: 9, Name: 'Furniture', MasterTypeId: parseInt(masterTypeId) },
                    { MasterId: 10, Name: 'Computer Equipment', MasterTypeId: parseInt(masterTypeId) }
                ];
            } else if (masterTypeName === 'capital') {
                sampleMasters = [
                    { MasterId: 11, Name: 'Owner Capital', MasterTypeId: parseInt(masterTypeId) },
                    { MasterId: 12, Name: 'Partner Capital', MasterTypeId: parseInt(masterTypeId) }
                ];
            } else if (masterTypeName === 'bank') {
                sampleMasters = [
                    { MasterId: 13, Name: 'Federal Bank', MasterTypeId: parseInt(masterTypeId) },
                    { MasterId: 14, Name: 'State Bank Of India', MasterTypeId: parseInt(masterTypeId) },
                    { MasterId: 15, Name: 'HDFC Bank', MasterTypeId: parseInt(masterTypeId) }
                ];
            } else if (masterTypeName === 'other') {
                sampleMasters = [
                    { MasterId: 16, Name: 'Miscellaneous', MasterTypeId: parseInt(masterTypeId) }
                ];
            } else if (masterTypeName === 'staff') {
                sampleMasters = [
                    { MasterId: 17, Name: 'Employee A', MasterTypeId: parseInt(masterTypeId) },
                    { MasterId: 18, Name: 'Employee B', MasterTypeId: parseInt(masterTypeId) }
                ];
            }

            setFilteredMasters(sampleMasters);
            setSuccess('Demo data loaded (API not available)');
        } finally {
            setLoadingMasters(false); // Use separate loading state
        }
    };

    const fetchLedgerData = async () => {
        if (!selectedMaster) return;
        if (new Date(fromDate) > new Date(toDate)) { setError('From Date cannot be greater than To Date'); return; }

        try {
            setLoadingLedger(true); // Use separate loading state to avoid page blink
            setLoadingMessage('Loading ledger data...');
            setLedgerData([]);
            // Fetch ledger data from API
            const response = await fetchData('ReportApi', `/LedgerDetails/${companyId}/${selectedMaster}/${fromDate}/${toDate}`, token);
            const apiData = response.response_data || response || [];

            // Filter out duplicate opening balance entries - keep only the first one
            const filteredData = apiData.filter((item, index, array) => {
                const description = (item.description1 || '').toLowerCase();
                const isOpeningBalance = description.includes('opening balance');

                if (isOpeningBalance) {
                    // Find the first occurrence of opening balance
                    const firstOpeningBalanceIndex = array.findIndex(
                        entry => (entry.description1 || '').toLowerCase().includes('opening balance')
                    );
                    // Keep only if this is the first opening balance entry
                    return index === firstOpeningBalanceIndex;
                }

                return true; // Keep all other entries
            });

            setLedgerData(filteredData);

        } catch (error) {
            console.error('Error fetching ledger data:', error);
            setError('Failed to load ledger data');
        } finally {
            setLoadingLedger(false); // Use separate loading state
        }
    };

    // Event handlers
    const handleMasterTypeChange = (e) => {
        const masterTypeId = e.target.value;
        setSelectedMasterType(masterTypeId);
        setSelectedMaster(''); // Reset master selection
        setLedgerData([]); // Clear ledger data
    };

    const handleMasterChange = (e) => {
        const masterId = e.target.value;
        setSelectedMaster(masterId);

        // Clear previous ledger data immediately
        setLedgerData([]);
    };

    // Helper function to fetch ledger data for a specific master
    const fetchLedgerDataForMaster = async (masterId) => {
        if (!masterId || !fromDate || !toDate) return;

        try {
            setLoadingLedger(true);
            setLoadingMessage('Loading ledger data...');

            // Fetch ledger data from API
            const response = await fetchData('ReportApi', `/LedgerDetails/${fromDate}/${toDate}/${companyId}/${masterId}/${selectedMasterType}`, token);
            const apiData = response.response_data || response || [];

            // Filter out duplicate opening balance entries - keep only the first one
            const filteredData = apiData.filter((item, index, array) => {
                const description = (item.description1 || '').toLowerCase();
                const isOpeningBalance = description.includes('opening balance');

                if (isOpeningBalance) {
                    // Find the first occurrence of opening balance
                    const firstOpeningBalanceIndex = array.findIndex(
                        entry => (entry.description1 || '').toLowerCase().includes('opening balance')
                    );
                    // Keep only if this is the first opening balance entry
                    return index === firstOpeningBalanceIndex;
                }

                return true; // Keep all other entries
            });

            setLedgerData(filteredData);

        } catch (error) {
            console.error('Error fetching ledger data:', error);
            setError('Failed to load ledger data');
        } finally {
            setLoadingLedger(false);
        }
    };

    const handleFromDateChange = (e) => {
        setFromDate(e.target.value);
    };

    const handleToDateChange = (e) => {
        setToDate(e.target.value);
    };

    const handleGetLedger = () => {
        if (selectedMaster && fromDate && toDate) {
            fetchLedgerData();
        } else {
            setError('Please select master and date range');
        }
    };

    const handleRefresh = () => {
        // Always clear the table first, then reload
        setLedgerData([]);
        if (selectedMaster) {
            fetchLedgerData();
        } else {
            fetchMasterTypes();
        }
    };

    const handlePrint = () => {
        window.print();
    };

    const handleExport = () => {
        if (!ledgerData.length) {
            setError('No data to export');
            return;
        }

        try {
            // Get selected master name
            const selectedMasterObj = filteredMasters.find(m => m.MasterId === selectedMaster);
            const masterName = selectedMasterObj?.Name || 'Unknown';

            // Get selected master type name
            const selectedMasterTypeObj = masterTypes.find(t => t.Id === parseInt(selectedMasterType));
            const masterTypeName = selectedMasterTypeObj?.Name || 'Unknown';

            // Format current date and time
            const now = new Date();
            const currentDate = now.toLocaleDateString('en-IN');
            const currentTime = now.toLocaleTimeString('en-IN');

            // Create HTML table with header sections
            let html = `
                <html>
                <head>
                    <meta charset="utf-8">
                    <style>
                        table { border-collapse: collapse; width: 100%; font-family: Arial, sans-serif; }
                        .header-section { text-align: center; font-weight: bold; font-size: 16px; padding: 10px; }
                        .info-section { padding: 5px; font-size: 12px; }
                        .info-label { font-weight: bold; }
                        th { background-color: #667eea; color: white; padding: 8px; text-align: left; border: 1px solid #ddd; font-size: 12px; }
                        td { padding: 6px; border: 1px solid #ddd; font-size: 11px; }
                        .number { text-align: right; }
                        .total-row { font-weight: bold; background-color: #f3f4f6; }
                    </style>
                </head>
                <body>
                    <table>
                        <!-- Header Section -->
                        <tr>
                            <td colspan="6" class="header-section">LEDGER REPORT</td>
                        </tr>
                        
                        <!-- Company and Branch Info -->
                        <tr>
                            <td colspan="6" class="info-section">
                                <span class="info-label">Company:</span> ${companyName || 'N/A'} | 
                                <span class="info-label">Branch:</span> Branch ${branchId || 'N/A'}
                            </td>
                        </tr>
                        
                        <!-- Master Info -->
                        <tr>
                            <td colspan="6" class="info-section">
                                <span class="info-label">Master Type:</span> ${masterTypeName} | 
                                <span class="info-label">Master Name:</span> ${masterName}
                            </td>
                        </tr>
                        
                        <!-- Date Range and User Info -->
                        <tr>
                            <td colspan="6" class="info-section">
                                <span class="info-label">From Date:</span> ${new Date(fromDate).toLocaleDateString('en-IN')} | 
                                <span class="info-label">To Date:</span> ${new Date(toDate).toLocaleDateString('en-IN')} | 
                                <span class="info-label">Generated On:</span> ${currentDate} ${currentTime} | 
                                <span class="info-label">Created By:</span> ${username || 'N/A'}
                            </td>
                        </tr>
                        
                        <!-- Empty Row -->
                        <tr><td colspan="6" style="height: 10px;"></td></tr>
                        
                        <!-- Table Headers -->
                        <tr>
                            <th>Sl No</th>
                            <th>Date</th>
                            <th>Description</th>
                            <th>Debit (₹)</th>
                            <th>Credit (₹)</th>
                            <th>Balance (₹)</th>
                        </tr>
            `;

            // Add table rows
            let runningBalance = 0;
            ledgerData.forEach((ledger, index) => {
                const debitAmount = parseFloat(ledger.debit) || 0;
                const creditAmount = parseFloat(ledger.credit) || 0;
                const description = (ledger.description1 || '').toLowerCase();
                const isTotal = description.includes('total');
                const isClosingBalance = description.includes('closing balance');

                // Calculate running balance for all entries except "Total"
                if (!isTotal) {
                    runningBalance += creditAmount - debitAmount;
                }

                const rowClass = (isTotal || isClosingBalance) ? 'total-row' : '';
                const balanceDisplay = (isTotal || isClosingBalance)
                    ? '-'
                    : `${Math.abs(runningBalance).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${runningBalance < 0 ? '(Dr)' : '(Cr)'}`;

                html += `
                    <tr class="${rowClass}">
                        <td>${index + 1}</td>
                        <td>${ledger.date1 ? new Date(ledger.date1).toLocaleDateString('en-IN') : '-'}</td>
                        <td>${ledger.description1 || '-'}</td>
                        <td class="number">${debitAmount > 0 ? debitAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}</td>
                        <td class="number">${creditAmount > 0 ? creditAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}</td>
                        <td class="number">${balanceDisplay}</td>
                    </tr>
                `;
            });

            html += `
                    </table>
                </body>
                </html>
            `;

            // Create blob and download
            const blob = new Blob([html], { type: 'application/vnd.ms-excel' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `Ledger_Report_${masterName}_${currentDate.replace(/\//g, '-')}.xls`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            setSuccess('Ledger report exported successfully');
        } catch (error) {
            console.error('Error exporting ledger report:', error);
            setError('Failed to export ledger report');
        }
    };

    const handleCloseSnackbar = () => {
        setError('');
        setSuccess('');
    };

    // Fetch data required for modals
    const fetchModalData = async () => {
        try {
            const [customersRes, itemsRes, banksRes, suppliersRes] = await Promise.allSettled([
                fetchData('ClientAndVendorApi', `/ClientList/${companyId}`, token),
                fetchData('ItemApi', `/${companyId}`, token),
                fetchData('BankApi', `/${companyId}`, token),
                fetchData('ClientAndVendorApi', `/VendorList/${companyId}`, token),
            ]);
            if (customersRes.status === 'fulfilled') setCustomers(customersRes.value.response_data || customersRes.value || []);
            if (itemsRes.status === 'fulfilled') setItems(itemsRes.value.response_data || itemsRes.value || []);
            if (banksRes.status === 'fulfilled') setBanks(banksRes.value.response_data || banksRes.value || []);
            if (suppliersRes.status === 'fulfilled') setSuppliers(suppliersRes.value.response_data || suppliersRes.value || []);
        } catch (error) {
            console.error('Error fetching modal data:', error);
        }
    };

    // Fetch sale data by ID
    const fetchSaleById = async (saleId) => {
        try {
            setModalLoading(true);
            const response = await fetchData('SaleApi', `/${saleId}`, token);
            const freshData = response.response_data || response;

            if (freshData) {
                const saleData = freshData.Sale || freshData;
                const details = freshData.SaleDetails || [];

                setSaleFormData({
                    Id: saleData.Id,
                    CompanyId: saleData.CompanyId || companyId,
                    BranchId: saleData.BranchId || branchId,
                    MasterId: saleData.MasterId,
                    CustomerName: saleData.CustomerName || '',
                    GSTNumber: saleData.GSTNumber || '',
                    InvoiceTypeId: saleData.InvoiceTypeId ?? null,
                    InvoiceTypeName: saleData.InvoiceTypeName || '',
                    StateId: saleData.StateId ?? null,
                    SalesManId: saleData.SalesManId ?? null,
                    BillNumber: saleData.BillNumber,
                    BillPrefix: saleData.BillPrefix || '',
                    FinYear: saleData.FinYear || '',
                    BillDate: saleData.BillDate?.split('T')[0] || new Date().toISOString().split('T')[0],
                    ClearingDate: saleData.ClearingDate?.split('T')[0] || '',
                    GstCalculationType: saleData.GstCalculationType || 1,
                    GstCalculationTypeName: saleData.GstCalculationTypeName || 'Excluding Tax',
                    Amount: saleData.Amount || 0,
                    DiscountAmount: saleData.DiscountAmount || 0,
                    TaxableAmount: saleData.TaxableAmount || 0,
                    CGSTAmount: saleData.CGSTAmount || 0,
                    SGSTAmount: saleData.SGSTAmount || 0,
                    IGSTAmount: saleData.IGSTAmount || 0,
                    FreightAmount: saleData.FreightAmount || 0,
                    FreightGSTRate: saleData.FreightGSTRate || 0,
                    FreightGSTAmount: saleData.FreightGSTAmount || 0,
                    PackingAmount: saleData.PackingAmount || 0,
                    PackingGSTRate: saleData.PackingGSTRate || 0,
                    PackingGSTAmount: saleData.PackingGSTAmount || 0,
                    TCSAmount: saleData.TCSAmount || 0,
                    RoundOff: saleData.RoundOff || 0,
                    TotalAmount: saleData.TotalAmount || saleData.GrandTotal || 0,
                    GrandTotal: saleData.GrandTotal || saleData.TotalAmount || 0,
                    CashAmount: saleData.CashAmount || 0,
                    UPIAmount: saleData.UPIAmount || 0,
                    CardAmount: saleData.CardAmount || 0,
                    BankId: saleData.BankId || null,
                    BalanceAmount: saleData.BalanceAmount || 0,
                    Remarks: saleData.Remarks || '',
                    DispatchFrom: saleData.DispatchFrom || '',
                    DispatchAddress1: saleData.DispatchAddress1 || '',
                    DispatchAddress2: saleData.DispatchAddress2 || '',
                    DispatchLocation: saleData.DispatchLocation || '',
                    DispatchPincode: saleData.DispatchPincode || '',
                    ShipTo: saleData.ShipTo || '',
                    ShipGSTNo: saleData.ShipGSTNo || '',
                    ShipToAddress1: saleData.ShipToAddress1 || '',
                    ShipToAddress2: saleData.ShipToAddress2 || '',
                    ShipToLocation: saleData.ShipToLocation || '',
                    ShipToPincode: saleData.ShipToPincode || '',
                    VehicleNumber: saleData.VehicleNumber || '',
                    TransporterName: saleData.TransporterName || '',
                    TransporterGST: saleData.TransporterGST || '',
                    Distance: saleData.Distance || 0,
                    IsVerified: saleData.IsVerified || false,
                    IsActive: saleData.IsActive ?? true,
                    SaleOrderId: saleData.SaleOrderId ?? null,
                    CreatedBy: saleData.CreatedBy,
                    CreatedDate: saleData.CreatedDate,
                    CreatedByName: saleData.CreatedByName,
                    ModifiedBy: saleData.ModifiedBy,
                    ModifiedDate: saleData.ModifiedDate,
                    ModifiedByName: saleData.ModifiedByName,
                });

                const mappedDetails = details.map(d => ({
                    Id: d.Id || 0,
                    ItemId: d.ItemId,
                    HSNCode: d.HSNCode || '',
                    GSTRate: d.GSTRate || 0,
                    Quantity: d.Quantity || 0,
                    Rate: d.Rate || 0,
                    Amount: d.Amount || 0,
                    DiscountPercentage: d.DiscountPercentage || 0,
                    DiscountAmount: d.DiscountAmount || 0,
                    TaxableAmount: d.TaxableAmount || 0,
                    CGSTAmount: d.CGSTAmount || 0,
                    SGSTAmount: d.SGSTAmount || 0,
                    IGSTAmount: d.IGSTAmount || 0,
                    TotalAmount: d.TotalAmount || 0,
                    IsActive: d.IsActive ?? true,
                    CreatedBy: d.CreatedBy,
                    CreatedDate: d.CreatedDate,
                    ModifiedBy: d.ModifiedBy,
                    ModifiedDate: d.ModifiedDate
                }));
                setSaleDetails(mappedDetails);

                // Auto-calculate summary totals from details (SaleModal doesn't recalculate in view mode)
                const totals = mappedDetails.reduce((acc, d) => ({
                    Amount: acc.Amount + (parseFloat(d.Amount) || 0),
                    DiscountAmount: acc.DiscountAmount + (parseFloat(d.DiscountAmount) || 0),
                    TaxableAmount: acc.TaxableAmount + (parseFloat(d.TaxableAmount) || 0),
                    CGSTAmount: acc.CGSTAmount + (parseFloat(d.CGSTAmount) || 0),
                    SGSTAmount: acc.SGSTAmount + (parseFloat(d.SGSTAmount) || 0),
                    IGSTAmount: acc.IGSTAmount + (parseFloat(d.IGSTAmount) || 0),
                }), { Amount: 0, DiscountAmount: 0, TaxableAmount: 0, CGSTAmount: 0, SGSTAmount: 0, IGSTAmount: 0 });

                setSaleFormData(prev => ({
                    ...prev,
                    Amount: prev.Amount || totals.Amount,
                    DiscountAmount: prev.DiscountAmount || totals.DiscountAmount,
                    TaxableAmount: prev.TaxableAmount || totals.TaxableAmount,
                    CGSTAmount: prev.CGSTAmount || totals.CGSTAmount,
                    SGSTAmount: prev.SGSTAmount || totals.SGSTAmount,
                    IGSTAmount: prev.IGSTAmount || totals.IGSTAmount,
                }));
            }
        } catch (error) {
            console.error('Error fetching sale data:', error);
            setError('Failed to load sale data');
        } finally {
            setModalLoading(false);
        }
    };

    // Fetch purchase data by ID
    const fetchPurchaseById = async (purchaseId) => {
        try {
            setModalLoading(true);
            const response = await fetchData('PurchaseApi', `/${purchaseId}`, token);
            const freshData = response.response_data || response;
            if (freshData) {
                const p = freshData.Purchase || freshData;
                const details = freshData.PurchaseDetails || [];
                setPurchaseFormData({
                    Id: p.Id, CompanyId: p.CompanyId || companyId, BranchId: p.BranchId || branchId,
                    MasterId: p.MasterId, MasterName: p.MasterName || '',
                    InvoiceTypeId: p.InvoiceTypeId || 0, InvoiceTypeName: p.InvoiceTypeName || '',
                    GSTNumber: p.GSTNumber || '',
                    BillDate: p.BillDate?.split('T')[0] || '', ClearingDate: p.ClearingDate?.split('T')[0] || '',
                    BillNumber: p.BillNumber || '', GstCalculationType: p.GstCalculationType || 1,
                    GstCalculationTypeName: p.GstCalculationTypeName || 'Excluding Tax',
                    Amount: p.Amount || 0, DiscountAmount: p.DiscountAmount || 0,
                    CGSTAmount: p.CGSTAmount || 0, SGSTAmount: p.SGSTAmount || 0, IGSTAmount: p.IGSTAmount || 0,
                    FreightAmount: p.FreightAmount || 0, FreightGSTRate: p.FreightGSTRate || 0,
                    FreightGSTAmount: p.FreightGSTAmount || 0, PackingAmount: p.PackingAmount || 0,
                    PackingGSTRate: p.PackingGSTRate || 0, PackingGSTAmount: p.PackingGSTAmount || 0,
                    TCSAmount: p.TCSAmount || 0, RoundOff: p.RoundOff || 0,
                    TotalAmount: p.TotalAmount || 0, PaidAmount: p.PaidAmount || 0,
                    BalanceAmount: p.BalanceAmount || 0, Remarks: p.Remarks || '',
                    IsActive: p.IsActive ?? true, IsVerified: p.IsVerified ?? false,
                    IsConvertToGoodReceipt: p.IsConvertToGoodReceipt ?? false,
                });
                setPurchaseDetails(details.map(d => ({
                    Id: d.Id || 0, ItemId: d.ItemId, ItemName: d.ItemName || '',
                    HSNCode: d.HSNCode || '', GSTRate: d.GSTRate || 0,
                    ReceivedQuantity: d.ReceivedQuantity || 0, BilledQuantity: d.BilledQuantity || 0,
                    DamageQuantity: d.DamageQuantity || 0, ExcessQuantity: d.ExcessQuantity || 0,
                    LessQuantity: d.LessQuantity || 0, StockQuantity: d.StockQuantity || 0,
                    Rate: d.Rate || 0, Amount: d.Amount || 0,
                    DiscountPercentage: d.DiscountPercentage || 0, DiscountAmount: d.DiscountAmount || 0,
                    TaxableAmount: d.TaxableAmount || 0, CGSTAmount: d.CGSTAmount || 0,
                    SGSTAmount: d.SGSTAmount || 0, IGSTAmount: d.IGSTAmount || 0,
                    TotalAmount: d.TotalAmount || 0, IsActive: d.IsActive ?? true,
                })));
            }
        } catch (error) {
            console.error('Error fetching purchase data:', error);  
            setError('Failed to load purchase data');
        } finally {
            setModalLoading(false);
        }
    };

    // Fetch payment/receipt data by ID
    const fetchPaymentById = async (paymentId) => {
        try {
            setModalLoading(true);

            // Fetch masters and payment methods if not loaded yet
            const [mastersRes, methodsRes] = await Promise.allSettled([
                paymentMasters.length ? Promise.resolve({ response_data: paymentMasters }) : fetchData('PaymentAndReceiptApi', `/Master/${companyId}`, token),
                paymentMethods.length ? Promise.resolve({ response_data: paymentMethods }) : fetchData('PaymentAndReceiptApi', `/PaymentModes/${companyId}`, token),
            ]);
            const loadedMasters = mastersRes.status === 'fulfilled' ? (mastersRes.value.response_data || mastersRes.value || []) : [];
            const loadedMethods = methodsRes.status === 'fulfilled' ? (methodsRes.value.response_data || methodsRes.value || []) : [];
            if (!paymentMasters.length) setPaymentMasters(loadedMasters);
            if (!paymentMethods.length) setPaymentMethods(loadedMethods);

            const response = await fetchData('PaymentAndReceiptApi', `/PaymentData/${paymentId}`, token);
            const data = response.response_data || response;
            if (data) {
                const p = data.PaymentAndReceipt || data;
                setPaymentFormData({
                    ...p,
                    BillDate: p.BillDate ? p.BillDate.split('T')[0] : '',
                    ChequeDate: p.ChequeDate ? p.ChequeDate.split('T')[0] : '',
                    MasterId: p.MasterId || '',
                });

                const purchaseDetails = data.PaymentAndReceiptPurchase || [];
                const saleDetails = data.PaymentAndReceiptSale || [];

                if (p.IsType && purchaseDetails.length > 0) {
                    setPaymentReceiptDetails(purchaseDetails.map(d => ({
                        Id: d.PurchaseId, Description: d.PurchaseDescription || '',
                        Amount: d.PurchaseAmount || 0, PaidAmount: d.Amount || 0,
                        BalanceAmount: 0, PendingAmount: 0, BillDate: d.BillDate || p.BillDate,
                        Type: 'Purchase', PaymentAmount: d.Amount || 0, IsSelected: true,
                    })));
                } else if (!p.IsType && saleDetails.length > 0) {
                    setPaymentReceiptDetails(saleDetails.map(d => ({
                        Id: d.SaleId, Description: d.SaleDescription || '',
                        Amount: d.SaleAmount || 0, PaidAmount: d.Amount || 0,
                        BalanceAmount: 0, PendingAmount: 0, BillDate: d.BillDate || p.BillDate,
                        Type: 'Sale', PaymentAmount: d.Amount || 0, IsSelected: true,
                    })));
                } else {
                    setPaymentReceiptDetails([{ noData: true }]);
                }
            }
        } catch (error) {
            console.error('Error fetching payment data:', error);
            setError('Failed to load payment/receipt data');
        } finally {
            setModalLoading(false);
        }
    };

    // Fetch journal entry data by ID
    const fetchJournalById = async (journalId) => {
        try {
            setModalLoading(true);
            // Fetch masters if not loaded
            if (journalMasters.length === 0) {
                const mastersRes = await fetchData('JournalEntryApi', `/Master/${companyId}`, token);
                setJournalMasters(mastersRes.response_data || mastersRes || []);
            }
            const response = await fetchData('JournalEntryApi', `/JournalEntryData/${journalId}`, token);
            const jd = response.response_data || response;
            const jEntry = jd.JournalEntry;
            if (jEntry) {
                setJournalFormData({
                    ...jEntry,
                    BillDate: jEntry.BillDate ? jEntry.BillDate.split('T')[0] : '',
                    IsType: jEntry.IsType === true ? 'Credit' : 'Debit',
                    MasterId: jEntry.MasterId || '',
                });
                const originalDetails = jd.JournalEntryDetails || [];
                setJournalEntryDetails(originalDetails.filter(d => d.MasterId !== jEntry.MasterId));
            }
        } catch (error) {
            console.error('Error fetching journal entry data:', error);
            setError('Failed to load journal entry data');
        } finally {
            setModalLoading(false);
        }
    };

    // Fetch purchase return data by ID
    const fetchPurchaseReturnById = async (id) => {
        try {
            setModalLoading(true);
            const response = await fetchData('PurchaseReturnApi', `/${id}`, token);
            const data = response.response_data || response;
            setPurchaseReturnData(data.PurchaseReturn || data);
            setPurchaseReturnDetails(data.PurchaseReturnDetails || []);
        } catch (error) {
            console.error('Error fetching purchase return data:', error);
            setError('Failed to load purchase return data');
        } finally {
            setModalLoading(false);
        }
    };

    // Fetch rent invoice data by ID
    const fetchRentInvoiceById = async (rentInvoiceId) => {
        try {
            setModalLoading(true);
            const response = await fetchData('RentInvoiceApi', `/${rentInvoiceId}`, token);
            const freshData = response.response_data || response;

            if (freshData) {
                // The API should return { RentInvoice: {...}, RentInvoiceDetails: [...] }
                setRentInvoiceData(freshData);
            }
        } catch (error) {
            console.error('Error fetching rent invoice data:', error);
            setError('Failed to load rent invoice data');
        } finally {
            setModalLoading(false);
        }
    };

    const handleDescriptionClick = async (ledger) => {
        // Skip if it's Opening Balance, Total, or Closing Balance
        const description = (ledger.description1 || '').toLowerCase();
        if (description.includes('opening balance') ||
            description.includes('total') ||
            description.includes('closing balance')) {
            return;
        }

        const tableName = ledger.tbl1;
        const recordId = ledger.id;

        if (!tableName || !recordId) return;

        console.log('[LedgerReport] Row clicked - tbl1:', tableName, '| id:', recordId);

        // Fetch modal data if not already loaded
        if (customers.length === 0 || items.length === 0 || banks.length === 0) {
            await fetchModalData();
        }

        // Open appropriate modal based on table name
        switch (tableName.toLowerCase()) {
            case 'sale':
                await fetchSaleById(recordId);
                setSelectedRecordId(recordId);
                setOpenSaleModal(true);
                break;
            case 'purchase':
                await fetchPurchaseById(recordId);
                setSelectedRecordId(recordId);
                setOpenPurchaseModal(true);
                break;
            case 'purchasereturn':
            case 'purchase return':
                await fetchPurchaseReturnById(recordId);
                setSelectedRecordId(recordId);
                setOpenPurchaseReturnModal(true);
                break;
            case 'journal':
                await fetchJournalById(recordId);
                setSelectedRecordId(recordId);
                setOpenJournalModal(true);
                break;
            case 'paymentandreceipt':
            case 'payment':
            case 'receipt':
                await fetchPaymentById(recordId);
                setSelectedRecordId(recordId);
                setOpenPaymentModal(true);
                break;
            case 'rentinvoice':
                await fetchRentInvoiceById(recordId);
                setSelectedRecordId(recordId);
                setOpenRentInvoiceModal(true);
                break;
            default:
                // Handle 'Payment NEFT', 'Payment Cash', 'Payment Cheque' etc.
                if (tableName.toLowerCase().startsWith('payment') || tableName.toLowerCase().startsWith('receipt')) {
                    await fetchPaymentById(recordId);
                    setSelectedRecordId(recordId);
                    setOpenPaymentModal(true);
                } else {
                    console.log('No modal configured for table:', tableName);
                }
                break;
        }
    };

    const handleCloseSaleModal = () => {
        setOpenSaleModal(false);
        setSelectedRecordId(null);
        setSaleFormData({});
        setSaleDetails([]);
    };

    const handleClosePurchaseModal = () => {
        setOpenPurchaseModal(false);
        setSelectedRecordId(null);
        setPurchaseFormData({});
        setPurchaseDetails([]);
    };

    const handleClosePaymentModal = () => {
        setOpenPaymentModal(false);
        setSelectedRecordId(null);
        setPaymentFormData({});
        setPaymentReceiptDetails([]);
    };

    const handleCloseJournalModal = () => {
        setOpenJournalModal(false);
        setSelectedRecordId(null);
        setJournalFormData({});
        setJournalEntryDetails([]);
    };

    const handleClosePurchaseReturnModal = () => {
        setOpenPurchaseReturnModal(false);
        setSelectedRecordId(null);
        setPurchaseReturnData(null);
        setPurchaseReturnDetails([]);
    };

    const handleCloseRentInvoiceModal = () => {
        setOpenRentInvoiceModal(false);
        setSelectedRecordId(null);
        setRentInvoiceData(null);
    };

    const handleSaveSale = () => {
        handleCloseSaleModal();
    };

    if (!mounted) return null;

    return (
        <Fade in={mounted} timeout={800}>
            <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 12, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>

                {loading && (
                    <Box sx={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        backgroundColor: 'rgba(10, 15, 30, 0.7)',
                        zIndex: 10000,
                        backdropFilter: 'blur(6px)',
                        WebkitBackdropFilter: 'blur(6px)',
                    }}>
                        <Box sx={{
                            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2.5,
                            background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.04) 100%)',
                            border: '1px solid rgba(255,255,255,0.12)',
                            borderRadius: '20px',
                            px: 5, py: 4,
                            boxShadow: '0 25px 60px rgba(0,0,0,0.5)',
                            minWidth: '220px',
                        }}>
                            {/* Dual-ring spinner */}
                            <Box sx={{ position: 'relative', width: 64, height: 64 }}>
                                <Box sx={{
                                    position: 'absolute', inset: 0,
                                    borderRadius: '50%',
                                    border: '3px solid transparent',
                                    borderTopColor: '#667eea',
                                    borderRightColor: '#667eea',
                                    animation: 'ledger-spin1 0.9s linear infinite',
                                    '@keyframes ledger-spin1': {
                                        '0%': { transform: 'rotate(0deg)' },
                                        '100%': { transform: 'rotate(360deg)' },
                                    },
                                }} />
                                <Box sx={{
                                    position: 'absolute', inset: 8,
                                    borderRadius: '50%',
                                    border: '3px solid transparent',
                                    borderTopColor: '#a78bfa',
                                    borderLeftColor: '#a78bfa',
                                    animation: 'ledger-spin2 0.7s linear infinite reverse',
                                    '@keyframes ledger-spin2': {
                                        '0%': { transform: 'rotate(0deg)' },
                                        '100%': { transform: 'rotate(360deg)' },
                                    },
                                }} />
                                {/* Center dot */}
                                <Box sx={{
                                    position: 'absolute', top: '50%', left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    width: 10, height: 10, borderRadius: '50%',
                                    background: 'linear-gradient(135deg, #667eea, #a78bfa)',
                                    boxShadow: '0 0 10px rgba(102,126,234,0.8)',
                                }} />
                            </Box>

                            <Typography sx={{
                                color: 'rgba(255,255,255,0.95)',
                                fontWeight: 600, fontSize: '0.95rem',
                                letterSpacing: '0.3px',
                                fontFamily: 'Times New Roman, serif',
                            }}>
                                {loadingMessage}
                            </Typography>

                            {/* Animated dots */}
                            <Box sx={{ display: 'flex', gap: 0.75 }}>
                                {[0, 1, 2].map(i => (
                                    <Box key={i} sx={{
                                        width: 6, height: 6, borderRadius: '50%',
                                        background: 'linear-gradient(135deg, #667eea, #a78bfa)',
                                        animation: 'ledger-bounce 1.2s ease-in-out infinite',
                                        animationDelay: `${i * 0.2}s`,
                                        '@keyframes ledger-bounce': {
                                            '0%, 80%, 100%': { transform: 'scale(0.6)', opacity: 0.4 },
                                            '40%': { transform: 'scale(1)', opacity: 1 },
                                        },
                                    }} />
                                ))}
                            </Box>
                        </Box>
                    </Box>
                )}

                <style>{`
          @keyframes slideIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .action-button {
            text-transform: none;
            font-weight: 600;
            letter-spacing: 0.5px;
          }
        `}</style>

                <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    {/* Toolbar */}
                    <div style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', background: 'linear-gradient(135deg, #5d4037 0%, #795548 100%)', gap: 6, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
                        <Tooltip title="Back" arrow>
                            <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={{ border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px', height: 29, width: 29, color: '#fff', flexShrink: 0, '&:hover': { background: 'rgba(255,255,255,0.1)' } }}>
                                <ArrowBack sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Tooltip>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>Ledger Report</span>
                        <div style={{ flex: 1, minWidth: 4 }} />
                        <Tooltip title="Refresh" arrow>
                            <IconButton onClick={handleRefresh} size="small" sx={{ border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px', height: 29, width: 29, color: '#fff', flexShrink: 0, '&:hover': { background: 'rgba(255,255,255,0.1)' } }}>
                                {loadingLedger ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <Refresh sx={{ fontSize: 16 }} />}
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Export" arrow>
                            <IconButton onClick={handleExport} disabled={!ledgerData.length} size="small" sx={{ border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px', height: 29, width: 29, color: '#fff', flexShrink: 0, '&:hover': { background: 'rgba(255,255,255,0.1)' }, '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' } }}>
                                <GetApp sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Tooltip>
                    </div>

                    {/* Filters Row */}
                    <div style={{ padding: '8px 12px', borderBottom: '1px solid #f0f0f0', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'thin' }}>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', minWidth: 'fit-content', paddingBottom: '4px' }}>
                            {/* From Date */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: isMobile ? '120px' : '140px' }}>
                                <label style={{ fontSize: '10px', color: '#666', paddingLeft: '4px' }}>From Date</label>
                                <TextField
                                    type="date"
                                    size="small"
                                    value={fromDate}
                                    onChange={handleFromDateChange}
                                    sx={{
                                        width: isMobile ? '120px' : '140px',
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '6px',
                                            backgroundColor: '#f8f9fa',
                                            fontSize: '11px',
                                            height: '32px',
                                            '&:hover fieldset': { borderColor: '#667eea' },
                                            '&.Mui-focused fieldset': { borderColor: '#667eea' },
                                        },
                                    }}
                                />
                            </div>

                            {/* To Date */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: isMobile ? '120px' : '140px' }}>
                                <label style={{ fontSize: '10px', color: '#666', paddingLeft: '4px' }}>To Date</label>
                                <TextField
                                    type="date"
                                    size="small"
                                    value={toDate}
                                    onChange={handleToDateChange}
                                    sx={{
                                        width: isMobile ? '120px' : '140px',
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '6px',
                                            backgroundColor: '#f8f9fa',
                                            fontSize: '11px',
                                            height: '32px',
                                            '&:hover fieldset': { borderColor: '#667eea' },
                                            '&.Mui-focused fieldset': { borderColor: '#667eea' },
                                        },
                                    }}
                                />
                            </div>

                            {/* Master Type Dropdown */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: isMobile ? '150px' : '200px' }}>
                                <label style={{ fontSize: '10px', color: '#666', paddingLeft: '4px' }}>Master Type</label>
                                <FormControl size="small" sx={{ minWidth: isMobile ? '150px' : '200px' }}>
                                    <Select
                                        value={selectedMasterType}
                                        onChange={handleMasterTypeChange}
                                        displayEmpty
                                        sx={{
                                            borderRadius: '6px',
                                            backgroundColor: '#f8f9fa',
                                            fontSize: '11px',
                                            height: '32px',
                                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#667eea' },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#667eea' },
                                        }}
                                    >
                                        <MenuItem value="" sx={{ fontSize: '11px' }}>Select Master Type</MenuItem>
                                        {masterTypes.map(type => (
                                            <MenuItem key={type.Id} value={type.Id} sx={{ fontSize: '11px' }}>
                                                {type.Name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </div>

                            {/* Master Dropdown */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: isMobile ? '180px' : '250px' }}>
                                <label style={{ fontSize: '10px', color: '#666', paddingLeft: '4px' }}>Master</label>
                                <Autocomplete
                                    size="small"
                                    freeSolo
                                    options={filteredMasters}
                                    getOptionLabel={(option) => {
                                        if (typeof option === 'string') return option;
                                        return option?.Name || '';
                                    }}
                                    value={filteredMasters.find(m => m.MasterId === selectedMaster) || null}
                                    onChange={(event, newValue) => {
                                        if (newValue && typeof newValue === 'object') {
                                            setSelectedMaster(newValue.MasterId);
                                            setLedgerData([]);
                                        } else {
                                            setSelectedMaster('');
                                            setLedgerData([]);
                                        }
                                    }}
                                    filterOptions={(options, { inputValue }) => {
                                        if (!inputValue) return options;
                                        const filterText = inputValue.toLowerCase();
                                        return options.filter(option =>
                                            (option.Name || '').toLowerCase().includes(filterText)
                                        );
                                    }}
                                    disabled={!selectedMasterType}
                                    isOptionEqualToValue={(option, value) => option.MasterId === value?.MasterId}
                                    renderOption={(props, option) => (
                                        <li {...props} key={option.MasterId} style={{ fontSize: '11px' }}>
                                            {option.Name}
                                        </li>
                                    )}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            size="small"
                                            placeholder={selectedMasterType ? 'Search Master...' : 'Select Master Type First'}
                                            sx={{
                                                '& .MuiInputBase-root': {
                                                    height: '32px',
                                                    minHeight: '32px',
                                                    padding: '0 9px !important',
                                                    borderRadius: '6px',
                                                    backgroundColor: selectedMasterType ? '#f8f9fa' : '#e5e7eb',
                                                    fontSize: '11px',
                                                },
                                                '& .MuiInputBase-input': {
                                                    padding: '0 !important',
                                                    fontSize: '11px',
                                                    height: '32px',
                                                },
                                                '& .MuiAutocomplete-endAdornment': {
                                                    top: '50%',
                                                    transform: 'translateY(-50%)',
                                                    right: '8px',
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: selectedMasterType ? '#667eea' : '#d1d5db' },
                                                '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#667eea' },
                                            }}
                                        />
                                    )}
                                    ListboxProps={{ style: { maxHeight: 200, fontSize: '11px' } }}
                                    sx={{ minWidth: isMobile ? '180px' : '250px' }}
                                />
                            </div>

                            {/* Get Ledger Button */}
                            <Tooltip title="Get ledger data" arrow>
                                <Button
                                    variant="contained"
                                    onClick={handleGetLedger}
                                    disabled={!selectedMaster || loadingLedger}
                                    className="action-button"
                                    style={{
                                        background: selectedMaster && !loadingLedger ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#e5e7eb',
                                        color: selectedMaster && !loadingLedger ? 'white' : '#9ca3af',
                                        padding: isMobile ? '0' : '4px 12px',
                                        borderRadius: '6px',
                                        fontSize: '12px',
                                        fontWeight: '700',
                                        boxShadow: selectedMaster && !loadingLedger ? '0 2px 8px rgba(102, 126, 234, 0.4)' : 'none',
                                        border: 'none',
                                        height: '32px',
                                        textTransform: 'none',
                                        letterSpacing: '0.5px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '4px',
                                        whiteSpace: 'nowrap',
                                        minWidth: isMobile ? '32px' : '100px',
                                        width: 'auto'
                                    }}
                                >
                                    {loadingLedger ? (
                                        <CircularProgress size={18} sx={{ color: '#9ca3af' }} />
                                    ) : (
                                        <SearchIcon style={{ fontSize: '18px' }} />
                                    )}
                                    {!isMobile && <span style={{ fontSize: '12px' }}>{loadingLedger ? 'Loading...' : 'Get Ledger'}</span>}
                                </Button>
                            </Tooltip>

                            {/* Export Button */}
                            <Tooltip title="Export report" arrow>
                                <Button
                                    variant="contained"
                                    onClick={handleExport}
                                    disabled={!ledgerData.length}
                                    className="action-button"
                                    style={{
                                        background: ledgerData.length ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' : '#e5e7eb',
                                        color: ledgerData.length ? 'white' : '#9ca3af',
                                        padding: isMobile ? '0' : '4px 12px',
                                        borderRadius: '6px',
                                        fontSize: '12px',
                                        fontWeight: '700',
                                        boxShadow: ledgerData.length ? '0 2px 8px rgba(245, 158, 11, 0.4)' : 'none',
                                        border: 'none',
                                        height: '32px',
                                        textTransform: 'none',
                                        letterSpacing: '0.5px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '4px',
                                        whiteSpace: 'nowrap',
                                        minWidth: isMobile ? '32px' : 'auto',
                                        width: 'auto'
                                    }}
                                >
                                    <GetApp style={{ fontSize: '18px' }} />
                                    {!isMobile && <span style={{ fontSize: '12px' }}>Export</span>}
                                </Button>
                            </Tooltip>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="report-table-scroll" style={{ overflow: 'auto', overflowY: 'scroll', maxHeight: 'calc(100vh - 130px)' }}>
                        <TableContainer component={Paper} style={{
                            boxShadow: 'none',
                            borderRadius: '0',
                            overflow: 'hidden'
                        }}>
                            <Table sx={{ minWidth: 650 }} size="small" aria-label="ledger report table">
                                <TableHead>
                                    <TableRow style={{
                                        background: '#795548',
                                        height: '24px'
                                    }}>
                                        <TableCell style={{
                                            color: 'white',
                                            fontWeight: '600',
                                            fontSize: '9px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.2px',
                                            padding: '2px 6px',
                                            textAlign: 'center',
                                            height: '24px',
                                            width: '5%'
                                        }}>
                                            Sl No
                                        </TableCell>
                                        <TableCell style={{
                                            color: 'white',
                                            fontWeight: '600',
                                            fontSize: '9px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.2px',
                                            padding: '2px 6px',
                                            textAlign: 'left',
                                            height: '24px',
                                            width: '10%'
                                        }}>
                                            Date
                                        </TableCell>
                                        <TableCell style={{
                                            color: 'white',
                                            fontWeight: '600',
                                            fontSize: '9px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.2px',
                                            padding: '2px 6px',
                                            textAlign: 'left',
                                            height: '24px',
                                            width: '35%'
                                        }}>
                                            Description
                                        </TableCell>
                                        <TableCell style={{
                                            color: 'white',
                                            fontWeight: '600',
                                            fontSize: '9px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.2px',
                                            padding: '2px 6px',
                                            textAlign: 'right',
                                            height: '24px',
                                            width: '13%'
                                        }}>
                                            Debit
                                        </TableCell>
                                        <TableCell style={{
                                            color: 'white',
                                            fontWeight: '600',
                                            fontSize: '9px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.2px',
                                            padding: '2px 6px',
                                            textAlign: 'right',
                                            height: '24px',
                                            width: '13%'
                                        }}>
                                            Credit
                                        </TableCell>
                                        <TableCell style={{
                                            color: 'white',
                                            fontWeight: '600',
                                            fontSize: '9px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.2px',
                                            padding: '2px 6px',
                                            textAlign: 'right',
                                            height: '24px',
                                            width: '14%'
                                        }}>
                                            Balance
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(() => {
                                        let runningBalance = 0;
                                        return ledgerData.map((ledger, index) => {
                                            const debitAmount = parseFloat(ledger.debit) || 0;
                                            const creditAmount = parseFloat(ledger.credit) || 0;

                                            // Check for special entries
                                            const description = (ledger.description1 || '').toLowerCase();
                                            const isTotal = description.includes('total');
                                            const isClosingBalance = description.includes('closing balance');

                                            // Calculate running balance for all entries except "Total"
                                            if (!isTotal) {
                                                runningBalance += creditAmount - debitAmount;
                                            }

                                            return (
                                                <TableRow
                                                    key={ledger.id || index}
                                                    sx={{
                                                        '&:nth-of-type(even)': { backgroundColor: '#efebe9' },
                                                        '&:hover': {
                                                            backgroundColor: '#f1f5f9',
                                                            transition: 'all 0.15s ease-in-out'
                                                        },
                                                        borderBottom: '1px solid #f1f5f9',
                                                        height: '28px'
                                                    }}
                                                >
                                                    <TableCell sx={{ py: 0.5, px: 0.75, textAlign: 'center' }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#6b7280', fontSize: '0.7rem' }}>
                                                            {index + 1}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell sx={{ py: 0.5, px: 0.75 }}>
                                                        <Typography variant="body2" sx={{ fontSize: '0.7rem', fontWeight: 500, color: '#374151' }}>
                                                            {ledger.date1 ? new Date(ledger.date1).toLocaleDateString('en-IN') : '-'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{
                                                            py: 0.5,
                                                            px: 0.75,
                                                            cursor: ledger.tbl1 && ledger.id &&
                                                                !['az', 'zy', 'zz'].includes((ledger.tbl1 || '').toLowerCase())
                                                                ? 'pointer' : 'default'
                                                        }}
                                                        onClick={() => handleDescriptionClick(ledger)}
                                                    >
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                fontSize: '0.7rem',
                                                                color: ledger.tbl1 && ledger.id &&
                                                                    !['az', 'zy', 'zz'].includes((ledger.tbl1 || '').toLowerCase())
                                                                    ? '#667eea' : '#374151',
                                                                fontWeight: ledger.tbl1 && ledger.id &&
                                                                    !['az', 'zy', 'zz'].includes((ledger.tbl1 || '').toLowerCase())
                                                                    ? 500 : 400,
                                                                '&:hover': ledger.tbl1 && ledger.id &&
                                                                    !['az', 'zy', 'zz'].includes((ledger.tbl1 || '').toLowerCase())
                                                                    ? {
                                                                        textDecoration: 'underline',
                                                                        color: '#764ba2'
                                                                    } : {}
                                                            }}
                                                        >
                                                            {ledger.description1 || '-'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell sx={{ py: 0.5, px: 0.75, textAlign: 'right' }}>
                                                        <Typography variant="body2" sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#dc2626' }}>
                                                            {debitAmount > 0 ? `₹${debitAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell sx={{ py: 0.5, px: 0.75, textAlign: 'right' }}>
                                                        <Typography variant="body2" sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#059669' }}>
                                                            {creditAmount > 0 ? `₹${creditAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell sx={{
                                                        py: 0.5,
                                                        px: 0.75,
                                                        textAlign: 'right',
                                                        backgroundColor: (isTotal || isClosingBalance) ? 'transparent' : (runningBalance >= 0 ? '#f0fdf4' : '#fef2f2')
                                                    }}>
                                                        <Typography variant="body2" sx={{
                                                            fontSize: '0.7rem',
                                                            fontWeight: 700,
                                                            color: (isTotal || isClosingBalance) ? '#6b7280' : (runningBalance >= 0 ? '#059669' : '#dc2626')
                                                        }}>
                                                            {(isTotal || isClosingBalance) ? '-' : (
                                                                `₹${Math.abs(runningBalance).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${runningBalance < 0 ? ' (Dr)' : ' (Cr)'}`
                                                            )}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        });
                                    })()}
                                    {ledgerData.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={6} sx={{ textAlign: 'center', py: 3 }}>
                                                <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.7rem' }}>
                                                    {selectedMaster ? 'No ledger entries found for the selected date range' : 'Select master and date range to view ledger entries'}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Closing Balance Summary */}
                        {ledgerData.length > 0 && (
                            <Box sx={{
                                mt: 1,
                                p: 1.5,
                                background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
                                borderRadius: '6px',
                                display: 'flex',
                                justifyContent: 'flex-end',
                                alignItems: 'center',
                                gap: 1.5
                            }}>
                                <Typography variant="body1" sx={{ fontWeight: 700, color: '#374151', fontSize: '0.8rem' }}>
                                    Closing Balance:
                                </Typography>
                                <Box sx={{
                                    px: 2,
                                    py: 0.75,
                                    borderRadius: '4px',
                                    backgroundColor: (() => {
                                        const closingBalanceEntry = ledgerData.find(ledger =>
                                            (ledger.description1 || '').toLowerCase().includes('closing balance')
                                        );
                                        if (closingBalanceEntry) {
                                            const debitAmount = parseFloat(closingBalanceEntry.debit) || 0;
                                            const creditAmount = parseFloat(closingBalanceEntry.credit) || 0;
                                            const balance = creditAmount - debitAmount;
                                            return balance >= 0 ? '#059669' : '#dc2626';
                                        }
                                        return '#6b7280';
                                    })()
                                }}>
                                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#fff', fontSize: '0.85rem' }}>
                                        {(() => {
                                            const closingBalanceEntry = ledgerData.find(ledger =>
                                                (ledger.description1 || '').toLowerCase().includes('closing balance')
                                            );
                                            if (closingBalanceEntry) {
                                                const debitAmount = parseFloat(closingBalanceEntry.debit) || 0;
                                                const creditAmount = parseFloat(closingBalanceEntry.credit) || 0;
                                                const balance = creditAmount - debitAmount;
                                                return `₹${Math.abs(balance).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${balance < 0 ? '(Dr)' : '(Cr)'}`;
                                            }
                                            return 'No closing balance found';
                                        })()}
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                    </div>
                </div>

                {/* Success/Error Snackbar */}
                <Snackbar
                    open={!!success}
                    autoHideDuration={3000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
                        {success}
                    </Alert>
                </Snackbar>

                <Snackbar
                    open={!!error}
                    autoHideDuration={3000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
                        {error}
                    </Alert>
                </Snackbar>

                {/* Sale Modal */}
                {openSaleModal && (
                    <SaleModal
                        open={openSaleModal}
                        onClose={handleCloseSaleModal}
                        modalAction="view"
                        formData={saleFormData}
                        setFormData={setSaleFormData}
                        saleDetails={saleDetails}
                        setSaleDetails={setSaleDetails}
                        onSave={handleCloseSaleModal}
                        customers={customers}
                        items={items}
                        banks={banks}
                        loading={modalLoading}
                        companyId={companyId}
                        branchId={branchId}
                        token={token}
                        onPrintInvoice={() => { }}
                    />
                )}

                {/* Purchase Modal */}
                {openPurchaseModal && (
                    <PurchaseModal
                        open={openPurchaseModal}
                        onClose={handleClosePurchaseModal}
                        modalAction="view"
                        formData={purchaseFormData}
                        setFormData={setPurchaseFormData}
                        purchaseDetails={purchaseDetails}
                        setPurchaseDetails={setPurchaseDetails}
                        onSave={handleClosePurchaseModal}
                        suppliers={suppliers}
                        items={items}
                        loading={modalLoading}
                        companyId={companyId}
                        token={token}
                        setLoading={() => { }}
                        setLoadingMessage={() => { }}
                    />
                )}

                {/* Payment/Receipt Modal */}
                {openPaymentModal && (
                    <PaymentReceiptsModal
                        open={openPaymentModal}
                        onClose={handleClosePaymentModal}
                        modalAction="view"
                        formData={paymentFormData}
                        setFormData={setPaymentFormData}
                        paymentReceiptDetails={paymentReceiptDetails}
                        setPaymentReceiptDetails={setPaymentReceiptDetails}
                        onSave={handleClosePaymentModal}
                        masters={paymentMasters}
                        banks={banks}
                        purchases={[]}
                        sales={[]}
                        paymentMethods={paymentMethods}
                        gstRates={[]}
                        loading={modalLoading}
                        companyId={companyId}
                        branchId={branchId}
                        token={token}
                    />
                )}

                {/* Journal Entry Modal */}
                {openJournalModal && (
                    <JournalEntryModal
                        open={openJournalModal}
                        onClose={handleCloseJournalModal}
                        modalAction="view"
                        formData={journalFormData}
                        setFormData={setJournalFormData}
                        journalEntryDetails={journalEntryDetails}
                        setJournalEntryDetails={setJournalEntryDetails}
                        onSave={handleCloseJournalModal}
                        masters={journalMasters}
                        gstRates={[]}
                        loading={modalLoading}
                        companyId={companyId}
                        branchId={branchId}
                        token={token}
                    />
                )}

                {/* Purchase Return View Modal */}
                {openPurchaseReturnModal && purchaseReturnData && (
                    <Dialog open={openPurchaseReturnModal} onClose={handleClosePurchaseReturnModal} maxWidth="xl" fullWidth
                        PaperProps={{ sx: { borderRadius: 3, maxHeight: '90vh' } }}>
                        <DialogTitle sx={{ borderBottom: '1px solid #e2e8f0', bgcolor: '#fafbfc', p: 2, pb: 1.5 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', fontSize: '1rem' }}>
                                        View Purchase Return
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.8rem' }}>
                                        Purchase return details and items
                                    </Typography>
                                </Box>
                                <IconButton onClick={handleClosePurchaseReturnModal} size="small" sx={{ bgcolor: '#f1f5f9', '&:hover': { bgcolor: '#e2e8f0' } }}>
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        </DialogTitle>
                        <DialogContent sx={{ p: 2, bgcolor: '#fafbfc' }}>
                            {/* Header Info */}
                            <Box sx={{ bgcolor: '#dbeafe', borderRadius: 2, p: 1.5, mb: 2, border: '1px solid #93c5fd' }}>
                                <Grid container spacing={1.5}>
                                    {[
                                        { label: 'Return No', value: purchaseReturnData.BillNumber },
                                        { label: 'Return Date', value: purchaseReturnData.BillDate ? new Date(purchaseReturnData.BillDate).toLocaleDateString('en-IN') : '-' },
                                        { label: 'Purchase No', value: purchaseReturnData.PurchaseNo || '-' },
                                        { label: 'Supplier', value: purchaseReturnData.MasterName || '-' },
                                        { label: 'Invoice Type', value: purchaseReturnData.InvoiceTypeName || '-' },
                                        { label: 'GST Type', value: purchaseReturnData.GstCalculationType === 2 ? 'Including Tax' : 'Excluding Tax' },
                                    ].map(({ label, value }) => (
                                        <Grid item xs={6} sm={4} md={2} key={label}>
                                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#1e40af', display: 'block', textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.5px', mb: 0.3 }}>
                                                {label}
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a', fontSize: '0.85rem' }}>
                                                {value}
                                            </Typography>
                                        </Grid>
                                    ))}
                                    {purchaseReturnData.Remarks && (
                                        <Grid item xs={12}>
                                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#1e40af', display: 'block', textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.5px', mb: 0.3 }}>Remarks</Typography>
                                            <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.85rem', fontStyle: 'italic' }}>{purchaseReturnData.Remarks}</Typography>
                                        </Grid>
                                    )}
                                </Grid>
                            </Box>

                            {/* Items Table */}
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mb: 1, fontSize: '0.9rem' }}>Return Items</Typography>
                            <Box sx={{ bgcolor: 'white', borderRadius: 1.5, overflow: 'hidden', border: '1px solid #e2e8f0', mb: 2 }}>
                                <Box sx={{ overflowX: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                                                {['#', 'Item', 'HSN', 'Qty', 'Rate', 'Amount', 'Disc%', 'Disc Amt', 'GST%', 'Taxable', 'CGST', 'SGST', 'IGST', 'Total'].map(h => (
                                                    <th key={h} style={{ padding: '8px 10px', textAlign: ['#', 'Item', 'HSN'].includes(h) ? 'left' : 'right', fontWeight: 700, color: 'white', fontSize: '0.65rem', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {purchaseReturnDetails.map((item, i) => (
                                                <tr key={item.Id || i} style={{ borderBottom: '1px solid #f1f5f9', backgroundColor: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                                                    <td style={{ padding: '7px 10px', fontSize: '0.78rem', color: '#6b7280' }}>{i + 1}</td>
                                                    <td style={{ padding: '7px 10px', fontSize: '0.78rem', fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap' }}>{item.ItemName}</td>
                                                    <td style={{ padding: '7px 10px', fontSize: '0.78rem', color: '#64748b' }}>{item.HSNCode || '-'}</td>
                                                    {[item.Quantity, item.Rate, item.Amount, item.DiscountPercentage, item.DiscountAmount, item.GSTRate, item.TaxableAmount, item.CGSTAmount, item.SGSTAmount, item.IGSTAmount, item.TotalAmount].map((v, vi) => (
                                                        <td key={vi} style={{ padding: '7px 10px', textAlign: 'right', fontSize: '0.78rem', color: '#374151', fontWeight: vi === 10 ? 700 : 400 }}>
                                                            {vi === 0 ? v : vi === 4 ? `${v || 0}%` : `₹${(parseFloat(v) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </Box>
                            </Box>

                            {/* Summary */}
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Box sx={{ bgcolor: 'white', border: '1px solid #e2e8f0', borderRadius: 2, p: 2, minWidth: 260 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mb: 1, fontSize: '0.85rem' }}>Summary</Typography>
                                    {[
                                        { label: 'Subtotal', value: purchaseReturnData.Amount, color: '#374151' },
                                        { label: 'Discount', value: purchaseReturnData.DiscountAmount, color: '#dc2626', prefix: '-' },
                                        { label: 'Taxable', value: purchaseReturnData.TaxableAmount, color: '#374151' },
                                        { label: 'CGST', value: purchaseReturnData.CGSTAmount, color: '#374151' },
                                        { label: 'SGST', value: purchaseReturnData.SGSTAmount, color: '#374151' },
                                        { label: 'IGST', value: purchaseReturnData.IGSTAmount, color: '#374151' },
                                        { label: 'Freight', value: purchaseReturnData.FreightAmount, color: '#374151' },
                                        { label: 'Packing', value: purchaseReturnData.PackingAmount, color: '#374151' },
                                        { label: 'TCS', value: purchaseReturnData.TCSAmount, color: '#374151' },
                                        { label: 'Round Off', value: purchaseReturnData.RoundOff, color: '#374151' },
                                    ].map(({ label, value, color, prefix }) => (
                                        <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                            <Typography variant="caption" sx={{ color: '#64748b' }}>{label}</Typography>
                                            <Typography variant="caption" sx={{ fontWeight: 500, color }}>{prefix || ''}₹{(parseFloat(value) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                                        </Box>
                                    ))}
                                    <Divider sx={{ my: 1 }} />
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>Total</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#059669' }}>₹{(parseFloat(purchaseReturnData.TotalAmount) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </DialogContent>
                        <DialogActions sx={{ p: 1.5, borderTop: '1px solid #e2e8f0' }}>
                            <Button onClick={handleClosePurchaseReturnModal} variant="outlined" size="small" sx={{ textTransform: 'none', borderRadius: '6px' }}>Close</Button>
                        </DialogActions>
                    </Dialog>
                )}



                {/* Modal Loading Overlay */}
                {modalLoading && (
                    <Box sx={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        backgroundColor: 'rgba(10, 15, 30, 0.7)',
                        zIndex: 10000,
                        backdropFilter: 'blur(6px)',
                        WebkitBackdropFilter: 'blur(6px)',
                    }}>
                        <Box sx={{
                            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2.5,
                            background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.04) 100%)',
                            border: '1px solid rgba(255,255,255,0.12)',
                            borderRadius: '20px',
                            px: 5, py: 4,
                            boxShadow: '0 25px 60px rgba(0,0,0,0.5)',
                            minWidth: '220px',
                        }}>
                            {/* Dual-ring spinner */}
                            <Box sx={{ position: 'relative', width: 64, height: 64 }}>
                                <Box sx={{
                                    position: 'absolute', inset: 0,
                                    borderRadius: '50%',
                                    border: '3px solid transparent',
                                    borderTopColor: '#667eea',
                                    borderRightColor: '#667eea',
                                    animation: 'ledger-spin1 0.9s linear infinite',
                                    '@keyframes ledger-spin1': {
                                        '0%': { transform: 'rotate(0deg)' },
                                        '100%': { transform: 'rotate(360deg)' },
                                    },
                                }} />
                                <Box sx={{
                                    position: 'absolute', inset: 8,
                                    borderRadius: '50%',
                                    border: '3px solid transparent',
                                    borderTopColor: '#a78bfa',
                                    borderLeftColor: '#a78bfa',
                                    animation: 'ledger-spin2 0.7s linear infinite reverse',
                                    '@keyframes ledger-spin2': {
                                        '0%': { transform: 'rotate(0deg)' },
                                        '100%': { transform: 'rotate(360deg)' },
                                    },
                                }} />
                                {/* Center dot */}
                                <Box sx={{
                                    position: 'absolute', top: '50%', left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    width: 10, height: 10, borderRadius: '50%',
                                    background: 'linear-gradient(135deg, #667eea, #a78bfa)',
                                    boxShadow: '0 0 10px rgba(102,126,234,0.8)',
                                }} />
                            </Box>

                            <Typography sx={{
                                color: 'rgba(255,255,255,0.95)',
                                fontWeight: 600, fontSize: '0.95rem',
                                letterSpacing: '0.3px',
                                fontFamily: 'Times New Roman, serif',
                            }}>
                                Loading details...
                            </Typography>

                            {/* Animated dots */}
                            <Box sx={{ display: 'flex', gap: 0.75 }}>
                                {[0, 1, 2].map(i => (
                                    <Box key={i} sx={{
                                        width: 6, height: 6, borderRadius: '50%',
                                        background: 'linear-gradient(135deg, #667eea, #a78bfa)',
                                        animation: 'ledger-bounce 1.2s ease-in-out infinite',
                                        animationDelay: `${i * 0.2}s`,
                                        '@keyframes ledger-bounce': {
                                            '0%, 80%, 100%': { transform: 'scale(0.6)', opacity: 0.4 },
                                            '40%': { transform: 'scale(1)', opacity: 1 },
                                        },
                                    }} />
                                ))}
                            </Box>
                        </Box>
                    </Box>
                )}
            </div>
        </Fade>
    );
};

export default LedgerReport;


